import { and, eq, exists, ne, or } from 'drizzle-orm'
import {
  type PeriodPlTotals,
  parsePeriodPlTotals,
  serializePeriodPlTotals,
} from '../periodPl'
import { accountingPeriodTable, periodPlSummaryTable } from '../schema'
import type {
  AccountingPeriod,
  DatabaseWithTransaction,
  TransactionDatabase,
} from '../types'

/**
 * 保存済み期間合計または集計中の識別子を取得する。
 * @param params DB と期間。
 * @returns 期間範囲が一致する集計状態。未作成の場合は undefined。
 */
export const readPeriodPlSummary = async (params: {
  database: DatabaseWithTransaction
  period: AccountingPeriod
}) => {
  const [row] = await params.database.database
    .select()
    .from(periodPlSummaryTable)
    .where(eq(periodPlSummaryTable.accountingPeriodId, params.period.id))
    .limit(1)
  if (
    row === undefined ||
    row.start.getTime() !== params.period.start ||
    row.end.getTime() !== params.period.end
  )
    return undefined
  return {
    generation: row.generation,
    totals:
      row.totals === null
        ? undefined
        : parsePeriodPlTotals({ text: row.totals }),
  }
}

/**
 * 既存データの補完を開始する。進行中の同期間の識別子は共有する。
 * @param params DB と対象期間。
 * @returns 補完を公開するときに照合する状態。
 */
export const beginPeriodPlSummary = async (params: {
  database: DatabaseWithTransaction
  period: AccountingPeriod
}) => {
  const {
    database: { database },
    period,
  } = params
  const values = {
    accountingPeriodId: period.id,
    start: new Date(period.start),
    end: new Date(period.end),
    generation: crypto.randomUUID(),
    totals: null,
  }
  await database
    .insert(periodPlSummaryTable)
    .values(values)
    .onConflictDoUpdate({
      target: periodPlSummaryTable.accountingPeriodId,
      set: values,
      setWhere: or(
        ne(periodPlSummaryTable.start, values.start),
        ne(periodPlSummaryTable.end, values.end),
      ),
    })
  return await readPeriodPlSummary(params)
}

/**
 * 補完中に元データが変わっていない場合だけ合計を公開する。
 * @param params DB、期間、開始時の識別子、合計。
 * @returns 公開に成功した場合は true。変更があった場合は false。
 */
export const publishPeriodPlSummary = async (params: {
  database: DatabaseWithTransaction
  period: AccountingPeriod
  generation: string
  totals: PeriodPlTotals
}): Promise<boolean> => {
  const rows = await params.database.database
    .update(periodPlSummaryTable)
    .set({ totals: serializePeriodPlTotals({ totals: params.totals }) })
    .where(
      and(
        eq(periodPlSummaryTable.accountingPeriodId, params.period.id),
        eq(periodPlSummaryTable.generation, params.generation),
        eq(periodPlSummaryTable.start, new Date(params.period.start)),
        eq(periodPlSummaryTable.end, new Date(params.period.end)),
        exists(
          params.database.database
            .select({ id: accountingPeriodTable.id })
            .from(accountingPeriodTable)
            .where(
              and(
                eq(accountingPeriodTable.id, params.period.id),
                eq(accountingPeriodTable.start, new Date(params.period.start)),
                eq(accountingPeriodTable.end, new Date(params.period.end)),
              ),
            ),
        ),
      ),
    )
    .returning({ id: periodPlSummaryTable.accountingPeriodId })
  return rows.length === 1
}

/**
 * 計算完了時に、最終取引と対応する集計を同じ保存トランザクションで確定する。
 * @param params 保存トランザクション、対象期間、計算済みの合計。
 */
export const saveComputedPeriodPlSummary = async (params: {
  database: TransactionDatabase
  period: AccountingPeriod
  totals: PeriodPlTotals
}): Promise<void> => {
  const values = {
    accountingPeriodId: params.period.id,
    start: new Date(params.period.start),
    end: new Date(params.period.end),
    generation: crypto.randomUUID(),
    totals: serializePeriodPlTotals({ totals: params.totals }),
  }
  await params.database
    .insert(periodPlSummaryTable)
    .values(values)
    .onConflictDoUpdate({
      target: periodPlSummaryTable.accountingPeriodId,
      set: values,
    })
}
